import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Music, MessageSquare, Users, MapPin, Share2, Eye } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MyFunerals = () => {
  const [funerals, setFunerals] = useState([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();
  const { token } = useAuth();

  useEffect(() => {
    const fetchFunerals = async () => {
      try {
        const response = await axios.get(`${API}/funerals`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setFunerals(response.data);
      } catch (error) {
        console.error('Failed to fetch funerals:', error);
        toast({
          title: 'Error',
          description: 'Failed to load funerals',
          variant: 'destructive',
        });
      } finally {
        setLoading(false);
      }
    };
    fetchFunerals();
  }, [token]);

  const handleShare = (funeral) => {
    const shareUrl = `${window.location.origin}/funeral/${funeral.share_code}`;
    navigator.clipboard.writeText(shareUrl);
    toast({
      title: 'Link Copied!',
      description: 'Share link copied to clipboard',
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-xl text-slate-600">Loading your funerals...</p>
        </div>
      </div>
    );
  }

  if (funerals.length === 0) {
    return (
      <div className="min-h-screen bg-slate-50 py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl font-bold text-slate-900 mb-4">My Funerals</h1>
          <p className="text-xl text-slate-600 mb-8">You haven't created any funeral plans yet</p>
          <Link to="/create">
            <Button size="lg" className="bg-amber-600 hover:bg-amber-700">
              Create Your First Funeral
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-4xl font-bold text-slate-900 mb-2">My Funerals</h1>
            <p className="text-lg text-slate-600">Manage your legendary sendoffs</p>
          </div>
          <Link to="/create">
            <Button className="bg-amber-600 hover:bg-amber-700">
              Create New Funeral
            </Button>
          </Link>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {funerals.map((funeral) => (
            <Card key={funeral.id} className="hover:shadow-xl transition-shadow duration-300">
              <CardHeader>
                <CardTitle className="text-2xl">{funeral.name}</CardTitle>
                <CardDescription>
                  Created on {new Date(funeral.created_at).toLocaleDateString()}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div className="flex items-center gap-2">
                    <Music className="w-4 h-4 text-amber-600" />
                    <span>{funeral.song_list?.length || 0} songs</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <MessageSquare className="w-4 h-4 text-blue-600" />
                    <span>{funeral.quotes?.length || 0} quotes</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Users className="w-4 h-4 text-purple-600" />
                    <span>{funeral.attendance_level}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-green-600" />
                    <span>Location set</span>
                  </div>
                </div>

                <div className="pt-4 border-t flex gap-2">
                  <Link to={`/funeral/${funeral.share_code}`} className="flex-1">
                    <Button variant="outline" className="w-full">
                      <Eye className="w-4 h-4 mr-2" />
                      View Details
                    </Button>
                  </Link>
                  <Button
                    variant="outline"
                    onClick={() => handleShare(funeral)}
                    className="flex-1"
                  >
                    <Share2 className="w-4 h-4 mr-2" />
                    Share
                  </Button>
                </div>

                <div className="bg-slate-100 p-3 rounded text-xs font-mono">
                  Share Code: {funeral.share_code}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
};

export default MyFunerals;